import { useState, useEffect, useCallback, useMemo } from 'react'
import { getMicrocopy, getNotificationTier, getNotificationMessage } from '../utils/microcopyEngine'
import { useLanguage } from '../context/LanguageContext'

/**
 * React hook for the Microcopy Engine
 * Provides dynamic, bilingual motivational messages based on user context
 * 
 * @param {Object} context - User context data
 * @returns {Object} { headline, subline, group, refresh }
 */
export function useMicrocopy(context = {}) {
  const { locale } = useLanguage()
  const [copy, setCopy] = useState({ headline: '', subline: '', group: '' })
  const [refreshKey, setRefreshKey] = useState(0)
  
  // Memoize context to prevent unnecessary recalculations
  const stableContext = useMemo(() => ({
    userLevelScore: context.userLevelScore ?? 0,
    streakDays: context.streakDays ?? 0,
    hoursSinceLastOpen: context.hoursSinceLastOpen ?? 0,
    todayCompleted: context.todayCompleted ?? 0,
    todayTotal: context.todayTotal ?? 0,
    daysToRecord: context.daysToRecord ?? null
  }), [
    context.userLevelScore,
    context.streakDays,
    context.hoursSinceLastOpen,
    context.todayCompleted,
    context.todayTotal,
    context.daysToRecord
  ])
  
  useEffect(() => {
    const newCopy = getMicrocopy(stableContext, locale)
    setCopy(newCopy)
  }, [stableContext, locale, refreshKey])
  
  // Manual refresh function to get a new random message
  const refresh = useCallback(() => {
    setRefreshKey(k => k + 1)
  }, [])
  
  return { ...copy, refresh }
}

/**
 * Hook for notification messages
 * 
 * @param {number} hoursSinceLastOpen
 * @param {Object} variables - Additional variables for smart notifications
 * @returns {Object} { tier, message, shouldNotify }
 */
export function useNotificationMessage(hoursSinceLastOpen, variables = {}) {
  const { locale } = useLanguage()
  
  return useMemo(() => {
    const tier = getNotificationTier(hoursSinceLastOpen)
    
    if (!tier) {
      return { tier: null, message: '', shouldNotify: false }
    }
    
    // Try smart notification first if variables are available
    let message = ''
    if (variables.streakDays || variables.daysToRecord) {
      message = getNotificationMessage('NOTIF_SMART', locale, {
        ...variables,
        hoursSinceLastOpen: Math.round(hoursSinceLastOpen)
      })
    }
    
    // Fall back to tier-specific message
    if (!message) {
      message = getNotificationMessage(tier, locale)
    }
    
    return { tier, message, shouldNotify: true }
  }, [hoursSinceLastOpen, variables, locale])
}

/**
 * Calculate hours since a given timestamp
 */
export function calculateHoursSince(timestamp) {
  if (!timestamp) return 0
  const now = new Date()
  const then = new Date(timestamp)
  return Math.max(0, (now - then) / (1000 * 60 * 60))
}

/**
 * Calculate days to beat personal record
 */
export function calculateDaysToRecord(currentStreak, personalBest) {
  if (!personalBest || currentStreak >= personalBest) return null
  return personalBest - currentStreak
}
